import React, { forwardRef, useEffect, useImperativeHandle, useRef, useCallback } from 'react';
import styles from './style.module.scss';
import AppleMap from '@zoocasa/node-kit/map/apple/map';
import getMap from '@zoocasa/node-kit/map/get-map';
import { loadMapKitCore } from 'utils/apple-maps/mapkit-core';
import { Bounds, LatLngPoint } from 'components/search-es/types';
import { getAppleMapBoundingRegion, getNextZoomWithPrecisionChange, getNextZoomWithPrecisionChangeMobile, calculateCenterPoint } from 'components/search-es/geo_helper';
import type { CommercialMapPageCluster, CommercialPropertyClusterItem } from '@zoocasa/go-search';
import { createRoot } from 'react-dom/client';
import CommercialCallout from './AnnotationCallout';
import { IS_MOBILE_BREAKPOINT } from 'hooks/use-size-class';
import MapControls from 'components/search-es/map/MapControls';
import SatelliteControl from 'components/search-es/map/SatelliteControl';
import LoadingSpinner from 'components/loading-spinner';
import { roundCoordinate } from 'utils/round-to-precision';

const MAP_ELEMENT_ID = 'commercial-search-map';
const CALLOUT_THRESHOLD = 24;
const APPEARANCE_ANIMATION = `${styles['scale-and-fadein']} .5s ease-in-out forwards`;

// Prevent scrolling inside the callout from zooming the map
const handleWheelEvent = (event: WheelEvent) => {
  event.stopImmediatePropagation();
};

export type CommercialMapProps = {
  clusters: CommercialMapPageCluster[];
  center: LatLngPoint;
  region: mapkit.CoordinateRegion | undefined;
  zoom: number;
  showsLoading?: boolean;
  disableCallout?: boolean;
  onClusterClick: (listings: CommercialPropertyClusterItem[]) => void;
  onMapBoundingRegionChanged: (oldBounds: Bounds | null, newBounds: Bounds, oldCenter: LatLngPoint | null, newCenter: LatLngPoint) => void;
  onMapZoomChanged: (oldZoom: number, newZoom: number, currentCenter?: LatLngPoint) => void;
  onFilterIconClick: () => void;
};

export type CommercialMapMethods = {
  changeLocation(location: LatLngPoint, zoom: number): void;
  clearAnnotations(): void;
};

type AnnotationData = {
  id: string;
  key: string;
  count: number;
  listings: CommercialPropertyClusterItem[];
};


function annotationFactory(_coordinate: mapkit.Coordinate, options: mapkit.AnnotationConstructorOptions) {
  const { data } = options as { data: AnnotationData };
  const div = document.createElement('div');
  div.className = styles.pin;
  div.setAttribute('data-key', data.key);
  div.setAttribute('data-type', 'commercial-cluster');
  div.textContent = `${data.count}`;
  return div;
}

function createCalloutDelegate(disableCallout: boolean) {
  const calloutDelegate: mapkit.AnnotationCalloutDelegate = {
    calloutElementForAnnotation: (annotation: mapkit.Annotation) => {
      const container = document.createElement('div');
      const { listings, count } = annotation.data as AnnotationData;
      container.className = styles['callout-root'];
      const root = createRoot(container);
      const handleClose = () => { annotation.selected = false; };
      root.render(<CommercialCallout count={count} listings={listings} onClose={handleClose} />);
      // Stop wheel events from bubbling to the map (prevents zooming while scrolling the callout)
      container.addEventListener('wheel', handleWheelEvent as any);
      return container;
    },
    calloutShouldAppearForAnnotation: (annotation: mapkit.Annotation) => {
      if (disableCallout) return false;
      const { count, listings } = annotation.data as AnnotationData;
      // if all listings have the same location then force callout to appear
      const positions = listings.map(l => l.position);
      const uniquePositions = [...new Set(positions)];
      return count <= CALLOUT_THRESHOLD || uniquePositions.length === 1;
    },
  };

  return calloutDelegate;
}

const CommercialMap = forwardRef<CommercialMapMethods, CommercialMapProps>(function CommercialMap({
  clusters,
  center,
  region,
  zoom,
  showsLoading = false,
  disableCallout,
  onMapBoundingRegionChanged,
  onMapZoomChanged,
  onFilterIconClick,
  onClusterClick,
}: CommercialMapProps, ref) {
  const mapRef = useRef<AppleMap | null>(null);
  const boundsRef = useRef<Bounds | null>(null);
  const zoomRef = useRef<number>(zoom);
  const isMapReady = useRef<boolean>(false);
  const lastCenterRef = useRef<mapkit.CoordinateRegion | null>(null);

  useImperativeHandle(ref, () => ({
    changeLocation(location, newZoom) {
      mapRef?.current?.changeLocation(location, newZoom);
    },
    clearAnnotations() {
      mapRef?.current?.clearMarkers();
    },
  }), []);

  const handlePan = useCallback((event: any) => {
    const oldBounds = boundsRef.current;
    const newBounds = getAppleMapBoundingRegion({ map: event.target } as AppleMap);
    boundsRef.current = newBounds;
    
    // Calculate center from bounds for URL update
    const newCenter = calculateCenterPoint(newBounds);
    const oldCenter = oldBounds ? calculateCenterPoint(oldBounds) : null;
    
    // Check if this is likely an invalid initial bounds calculation
    // If the calculated center is very far from the expected center, it's probably wrong
    // This can happen when the map first initializes and hasn't settled to the correct position yet
    const latDiff = Math.abs(newCenter.lat - center.lat);
    const lngDiff = Math.abs(newCenter.lng - center.lng);
    const isLikelyInvalid = latDiff > 1 || lngDiff > 1; // More than 1 degree difference is suspicious
    
    // If the calculated center is suspiciously different from expected, use the expected center instead
    // This prevents incorrect centers from being set during map initialization
    const centerToUse = isLikelyInvalid ? center : newCenter;
    
    onMapBoundingRegionChanged?.(oldBounds, newBounds, oldCenter, centerToUse);
  }, [onMapBoundingRegionChanged, center, zoom]);

  const handleZoom = useCallback((event: any) => {
    const oldZoom = zoomRef.current;
    const newZoom = event.target._impl.zoomLevel || 0;
    zoomRef.current = newZoom;
    
    // When zoom changes, the visual center may shift (especially when zooming toward cursor)
    // Get the current map center from bounds to ensure we capture the actual center
    const currentBounds = getAppleMapBoundingRegion({ map: event.target } as AppleMap);
    const currentCenter = calculateCenterPoint(currentBounds);
    boundsRef.current = currentBounds;
    
    onMapZoomChanged?.(oldZoom, newZoom, currentCenter);
  }, [onMapZoomChanged]);

  const handleZoomInClick = useCallback(() => {
    mapRef?.current?.zoomIn();
  }, []);

  const handleZoomOutClick = useCallback(() => {
    mapRef?.current?.zoomOut();
  }, []);

  const handleGetUserLocationClick = useCallback(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      position => {
        const { coords } = position;
        mapRef?.current?.changeLocation({ lng: coords.longitude, lat: coords.latitude }, 14);
      },
      () => { }
    );
  }, []);

  const handleSatelliteAndStreetViewToggled = useCallback(() => {
    if (mapRef?.current !== null) {
      if (mapRef.current?.map.mapType === mapkit.Map.MapTypes.Standard) {
        mapRef.current.map.mapType = mapkit.Map.MapTypes.Satellite;
        return mapkit.Map.MapTypes.Satellite;
      } else if (mapRef.current?.map.mapType === mapkit.Map.MapTypes.Satellite) {
        mapRef.current.map.mapType = mapkit.Map.MapTypes.Standard;
        return mapkit.Map.MapTypes.Standard;
      }
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      await loadMapKitCore();
      if (window.mapkit && mapRef.current === null) {
        mapRef.current = getMap('apple') as AppleMap;
        mapRef.current.drawMap({ latitude: center.lat, longitude: center.lng, zoom, isRotationEnabled: false }, MAP_ELEMENT_ID);
        if (mapRef.current.longitude === 0 || mapRef.current.latitude === 0) {
          mapRef.current.changeLocation(center, zoom);
        }
        mapRef.current.onPan(handlePan);
        mapRef.current.onZoom(handleZoom);
        isMapReady.current = true;
      }
      // Wait a bit for the map to settle before triggering initial handlePan
      // This prevents reading incorrect bounds during initialization
      if (mapRef.current) {
        setTimeout(() => {
          if (mapRef.current) {
            handlePan({ target: mapRef.current.map });
          }
        }, 100); // Small delay to let map settle
      }
    };
    init();
    return () => mapRef.current?.removeEventListeners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mapRef.current || !isMapReady.current || !region) return;
    const { center, span } = region;
  
    const target = {
      lat: roundCoordinate(center.latitude),
      lng: roundCoordinate(center.longitude),
      latDelta: roundCoordinate(span.latitudeDelta),
      lngDelta: roundCoordinate(span.longitudeDelta),
    };
    
    const last = lastCenterRef.current;
    const changed =
      !last ||
      last.center.latitude !== target.lat ||
      last.center.longitude !== target.lng ||
      last.span.latitudeDelta !== target.latDelta ||
      last.span.longitudeDelta !== target.lngDelta;
  
    if (changed) {
      mapRef.current?.map.setRegionAnimated(region, true);
      lastCenterRef.current = region;
    }
  }, [region]);

  // draw clusters
  useEffect(() => {
    if (!mapRef.current) return;
    const appleMap = mapRef.current;
    const annotations = appleMap.annotations || [];
    const clustersMap = new Map(clusters.map(c => [c.key, c]));

    // Remove annotations not present anymore
    if (annotations.length > 0) {
      const toRemove: mapkit.Annotation[] = annotations.filter((annotation: mapkit.Annotation) => {
        const element = annotation.element;
        if (element.getAttribute('data-type') === 'commercial-cluster') {
          const key = element.getAttribute('data-key');
          if (key && clustersMap.has(key)) {
            const isDifferentCount = clustersMap.get(key).count !== parseInt(element.textContent || '0');
            if (isDifferentCount) {
              return true;
            }
            clustersMap.delete(key);
            return false;
          }
          return true;
        }
        return false;
      });
      appleMap.removePins(toRemove);
    }

    // Add new clusters
    const calloutDelegate = createCalloutDelegate(disableCallout);
    const handleAnnotationSelection = (event: mapkit.EventBase<mapkit.Annotation>) => {
      if (event.type !== 'select') return;

      const { count, listings } = event.target.data as AnnotationData;
      const uniquePositions = [...new Set(listings.map(l => l.position))];

      if (count > CALLOUT_THRESHOLD && uniquePositions.length > 1) {
        const currentZoomLevel = mapRef.current?.zoom || 0;
        const useMobilePrecision = window.innerWidth < IS_MOBILE_BREAKPOINT;
        const adjustedZoom = useMobilePrecision ? (zoomRef.current ?? currentZoomLevel) : Math.floor(currentZoomLevel);
        const getNextZoomFn = useMobilePrecision ? getNextZoomWithPrecisionChangeMobile : getNextZoomWithPrecisionChange;
        const nextZoomLevel = getNextZoomFn(adjustedZoom);
        const newCenter = event.target.coordinate;
        mapRef.current?.zoomToRegion('in', adjustedZoom - nextZoomLevel, newCenter);
        setTimeout(() => {
          if (mapRef.current?.map.selectedAnnotation) {
            mapRef.current.map.selectedAnnotation.selected = false;
          }
        }, 0);
      } else {
        onClusterClick(listings);
      }

    };
    const newClusters = Array.from(clustersMap.values());
    newClusters.forEach(c => {
      const position = { lat: c.coordinates?.lat || 0, lng: c.coordinates?.lon || 0 };
      const data: AnnotationData = {
        id: c.key,
        key: c.key,
        count: c.count,
        listings: c.listings || [],
      };
      appleMap.addAnnotation(annotationFactory, data, calloutDelegate, position, APPEARANCE_ANIMATION, handleAnnotationSelection);
    });
  }, [clusters, disableCallout, onClusterClick]);

  return (
    <div className={styles.mapWrapper}>
      <div id={MAP_ELEMENT_ID} className={styles.map} />
      <MapControls
        isVisible={false}
        onGetUserLocationClick={handleGetUserLocationClick}
        onFilterClick={onFilterIconClick}
        onZoomInClick={handleZoomInClick}
        onZoomOutClick={handleZoomOutClick}
      />
      <SatelliteControl onViewToggle={handleSatelliteAndStreetViewToggled} />
      {showsLoading && <div className={styles['map-loading']}>Loading <LoadingSpinner className={styles['map-spinner']} /> </div>}
    </div>
  );
});

export default CommercialMap;

